// src/components/pdf-editor/PDFViewer.js
/**
 * PDF Viewer v2 – Server-Rendered Pages
 *
 * Displays page images from the backend (PyMuPDF-rendered PNGs).
 * Overlays interactive TextOverlay and ImageOverlay elements.
 * All coordinates in PDF points, scaled by zoom × displayScale.
 *
 * displayScale = displayWidth / page.width
 * where displayWidth is the natural image width / renderScale (2x)
 */
import React, { useCallback, useRef } from 'react';
import TextOverlay from './TextOverlay';
import ImageOverlay from './ImageOverlay';

const PDFViewer = ({
  pages, pageImages, zoom, elements, selectedId,
  onSelectElement, onDeselectAll, onUpdateElement, onInteractionEnd,
  mode, onCanvasClick,
}) => {
  const containerRef = useRef(null);

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '24px 0', gap: 24, minHeight: '100%',
      }}
      onClick={(e) => {
        // Click on background → deselect
        if (e.target === containerRef.current || e.target.dataset?.role === 'page-bg') {
          onDeselectAll();
        }
      }}
    >
      {pages.map((page) => (
        <PageView
          key={page.pageIndex}
          page={page}
          imageUrl={pageImages[page.pageIndex]}
          zoom={zoom}
          elements={elements.filter(el => el.pageIndex === page.pageIndex)}
          selectedId={selectedId}
          onSelectElement={onSelectElement}
          onUpdateElement={onUpdateElement}
          onInteractionEnd={onInteractionEnd}
          mode={mode}
          onCanvasClick={onCanvasClick}
        />
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  Single Page                                                               */
/* ═══════════════════════════════════════════════════════════════════════════ */
const PageView = ({
  page, imageUrl, zoom, elements, selectedId,
  onSelectElement, onUpdateElement, onInteractionEnd,
  mode, onCanvasClick,
}) => {
  const pageRef = useRef(null);

  // The page image is rendered at RENDER_SCALE × PDF size.
  // We display it at zoom × PDF size.
  const displayW = page.width * zoom;
  const displayH = page.height * zoom;

  // scale: PDF points → display pixels
  const scale = zoom;

  const handlePageClick = useCallback((e) => {
    if (mode === 'select') return;
    const rect = pageRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Convert click position to PDF coordinates
    const pdfX = (e.clientX - rect.left) / scale;
    const pdfY = (e.clientY - rect.top) / scale;
    onCanvasClick(page.pageIndex, pdfX, pdfY);
  }, [mode, scale, page.pageIndex, onCanvasClick]);

  return (
    <div
      ref={pageRef}
      data-role="page-bg"
      onClick={handlePageClick}
      style={{
        position: 'relative',
        width: displayW,
        height: displayH,
        background: '#fff',
        boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
        borderRadius: 4,
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {/* Page image (server-rendered) */}
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={`Page ${page.pageIndex + 1}`}
          draggable={false}
          style={{
            position: 'absolute', top: 0, left: 0,
            width: displayW, height: displayH,
            pointerEvents: 'none',
            imageRendering: 'auto',
          }}
        />
      ) : (
        <div style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#94a3b8', fontSize: 14,
        }}>
          Loading page {page.pageIndex + 1}…
        </div>
      )}

      {/* Overlay elements */}
      {elements.map((el) => {
        if (el.type === 'text') {
          return (
            <TextOverlay
              key={el.id}
              element={el}
              scale={scale}
              selected={el.id === selectedId}
              onSelect={() => onSelectElement(el.id)}
              onUpdate={onUpdateElement}
              onInteractionEnd={onInteractionEnd}
            />
          );
        }
        if (el.type === 'image') {
          return (
            <ImageOverlay
              key={el.id}
              element={el}
              scale={scale}
              selected={el.id === selectedId}
              onSelect={() => onSelectElement(el.id)}
              onUpdate={onUpdateElement}
              onInteractionEnd={onInteractionEnd}
            />
          );
        }
        return null;
      })}

      {/* Page number badge */}
      <div style={{
        position: 'absolute', bottom: 8, right: 12,
        fontSize: 11, color: '#94a3b8', fontWeight: 600,
        background: 'rgba(255,255,255,0.8)', padding: '2px 8px',
        borderRadius: 4, pointerEvents: 'none',
      }}>
        Page {page.pageIndex + 1}
      </div>
    </div>
  );
};

export default PDFViewer;
